<template>
    <section class="org-summary-card" aria-labelledby="organization-summary-card-title">
        <div class="org-summary-glow org-summary-glow-one"></div>
        <div class="org-summary-glow org-summary-glow-two"></div>
        <div class="org-summary-card-content">
            <div class="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3">
                <div class="d-flex align-items-start gap-3">
                    <div class="org-summary-icon" aria-hidden="true">
                        <i class="material-icons">insights</i>
                    </div>
                    <div>
                        <p class="org-summary-eyebrow mb-1">Organization command center</p>
                        <h4 id="organization-summary-card-title" class="org-summary-title mb-1">
                            See the full picture
                        </h4>
                        <p class="org-summary-copy mb-0">
                            Review the current organization and every location it contains in one beautiful,
                            operational snapshot.
                        </p>
                    </div>
                </div>
                <button type="button" class="org-summary-open-button" @click="$emit('open')">
                    <span>Open summary</span>
                    <i class="material-icons" aria-hidden="true">arrow_forward</i>
                </button>
            </div>

            <div class="org-summary-stats mt-4">
                <div class="org-summary-stat">
                    <span class="org-summary-stat-icon"><i class="material-icons">domain</i></span>
                    <span>
                        <strong>{{ formatNumber(organizations.length) }}</strong>
                        <small>{{ organizations.length === 1 ? 'organization' : 'organizations' }}</small>
                    </span>
                </div>
                <div class="org-summary-stat">
                    <span class="org-summary-stat-icon"><i class="material-icons">location_city</i></span>
                    <span>
                        <strong>{{ formatNumber(locationCount) }}</strong>
                        <small>{{ locationCount === 1 ? 'location' : 'locations' }}</small>
                    </span>
                </div>
                <div class="org-summary-stat">
                    <span class="org-summary-stat-icon"><i class="material-icons">volunteer_activism</i></span>
                    <span>
                        <strong>{{ formatNumber(volunteerCount) }}</strong>
                        <small>active volunteers</small>
                    </span>
                </div>
                <div class="org-summary-stat">
                    <span class="org-summary-stat-icon"><i class="material-icons">event_available</i></span>
                    <span>
                        <strong>{{ formatNumber(instanceCount) }}</strong>
                        <small>instances in 30 days</small>
                    </span>
                </div>
            </div>

            <div class="org-summary-footer mt-3">
                <span><i class="material-icons">auto_awesome</i> Live operational metrics</span>
                <span v-if="loading" class="org-summary-loading">
                    <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    Updating
                </span>
                <span v-else><i class="material-icons">shield</i> Admin-only view</span>
            </div>
        </div>
    </section>
</template>

<script setup>
import { computed } from 'vue';

defineEmits(['open']);

const props = defineProps({
    organizations: {
        type: Array,
        default: () => [],
    },
    loading: {
        type: Boolean,
        default: false,
    },
});

const locationCount = computed(() => props.organizations.reduce(
    (total, organization) => total + (organization.locations?.length || 0),
    0
));

const volunteerCount = computed(() => props.organizations.reduce(
    (total, organization) => total + Number(organization.volunteer_count || 0),
    0
));

const instanceCount = computed(() => props.organizations.reduce(
    (total, organization) => total + Number(organization.total_instances_next_30_days || 0),
    0
));

const formatNumber = (value) => new Intl.NumberFormat().format(Number(value || 0));
</script>

<style scoped>
.org-summary-card {
    position: relative;
    overflow: hidden;
    margin: 0 0 1.5rem;
    border: 1px solid rgba(111, 78, 183, 0.22);
    border-radius: 22px;
    background:
        radial-gradient(circle at 84% 10%, rgba(255, 255, 255, 0.2), transparent 25%),
        linear-gradient(125deg, #2d1b69 0%, #512da8 50%, #0f766e 125%);
    box-shadow: 0 16px 36px rgba(45, 27, 105, 0.23);
    color: #fff;
}

.org-summary-card-content {
    position: relative;
    z-index: 1;
    padding: 1.4rem 1.5rem 1.1rem;
}

.org-summary-glow {
    position: absolute;
    width: 210px;
    height: 210px;
    border-radius: 50%;
    opacity: 0.22;
    filter: blur(3px);
}

.org-summary-glow-one {
    top: -120px;
    right: 12%;
    background: #facc15;
}

.org-summary-glow-two {
    bottom: -145px;
    left: 22%;
    background: #5eead4;
}

.org-summary-icon {
    display: grid;
    width: 52px;
    height: 52px;
    flex-shrink: 0;
    place-items: center;
    border: 1px solid rgba(255, 255, 255, 0.28);
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.16);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.15);
}

.org-summary-icon i {
    font-size: 29px;
}

.org-summary-eyebrow {
    color: #ddd6fe;
    font-size: 0.68rem;
    font-weight: 800;
    letter-spacing: 0.12em;
    text-transform: uppercase;
}

.org-summary-title {
    color: #fff;
    font-size: 1.4rem;
    font-weight: 750;
}

.org-summary-copy {
    max-width: 700px;
    color: rgba(255, 255, 255, 0.78);
    font-size: 0.83rem;
    line-height: 1.5;
}

.org-summary-open-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    min-width: 155px;
    padding: 0.7rem 1rem;
    border: 0;
    border-radius: 12px;
    background: #fff;
    color: #3f287f;
    font-size: 0.78rem;
    font-weight: 800;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.org-summary-open-button:hover,
.org-summary-open-button:focus-visible {
    box-shadow: 0 8px 18px rgba(0, 0, 0, 0.18);
    transform: translateY(-2px);
}

.org-summary-open-button i {
    font-size: 18px;
}

.org-summary-stats {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.6rem;
}

.org-summary-stat {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    min-width: 0;
    padding: 0.7rem 0.75rem;
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: 14px;
    background: rgba(14, 13, 51, 0.18);
}

.org-summary-stat-icon {
    display: grid;
    width: 30px;
    height: 30px;
    flex-shrink: 0;
    place-items: center;
    border-radius: 9px;
    background: rgba(255, 255, 255, 0.15);
}

.org-summary-stat-icon i {
    font-size: 17px;
}

.org-summary-stat strong,
.org-summary-stat small {
    display: block;
}

.org-summary-stat strong {
    color: #fff;
    font-size: 1.05rem;
    line-height: 1.1;
}

.org-summary-stat small {
    overflow: hidden;
    color: rgba(255, 255, 255, 0.67);
    font-size: 0.65rem;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.org-summary-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    color: rgba(255, 255, 255, 0.68);
    font-size: 0.68rem;
}

.org-summary-footer span {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
}

.org-summary-footer i {
    font-size: 14px;
}

.org-summary-loading {
    color: #fef3c7;
}

@media (max-width: 767px) {
    .org-summary-card-content {
        padding: 1.15rem 1rem 0.95rem;
    }

    .org-summary-stats {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .org-summary-footer {
        align-items: flex-start;
        flex-direction: column;
    }
}
</style>
